import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Container,
    Box,
    Alert,
    Typography,
    Tabs,
    Tab,
    Paper,
} from '@mui/material';
import {
    BarChart as BarChartIcon,
    ReceiptLong,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import { getPowerCost, getAvailableZones } from '../api/powerCost';
import PowerCostControlBar from './powerCost/PowerCostControlBar';
import PowerCostKpiCards from './powerCost/PowerCostKpiCards';
import PowerCostCharts from './powerCost/PowerCostCharts';
import DigitalBillReceipt from './powerCost/DigitalBillReceipt';
import PowerCostBreakdownTable from './powerCost/PowerCostBreakdownTable';
import PowerCostLoadingSkeleton from './powerCost/PowerCostLoadingSkeleton';
import PowerCostEmptyState from './powerCost/PowerCostEmptyState';
import {
    calculatePowerCostSummary,
    buildAnnualCostChartData,
    DEFAULT_FT_RATE,
    DEFAULT_BILLING_DAY,
} from '../utils/powerCostUtils';

function PowerCostPage() {
    const { t } = useTranslation();

    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedMonth, setSelectedMonth] = useState('all'); // 'all' or 1..12
    const [billingDay, setBillingDay] = useState(DEFAULT_BILLING_DAY); // 15
    const [selectedZone, setSelectedZone] = useState('all');
    const [ftRate, setFtRate] = useState(DEFAULT_FT_RATE);

    // Active View Tab: 'overview' | 'receipt'
    const [activeTab, setActiveTab] = useState('overview');

    const [availableZones, setAvailableZones] = useState([]);
    const [rawRecords, setRawRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Fetch available zones on mount
    useEffect(() => {
        let isMounted = true;
        const fetchZones = async () => {
            const zones = await getAvailableZones();
            if (isMounted && zones.length > 0) {
                setAvailableZones(zones);
            }
        };
        fetchZones();
        return () => {
            isMounted = false;
        };
    }, []);

    // Fetch power cost records
    const fetchPowerCostData = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const data = await getPowerCost({
                year: selectedYear,
                month: selectedMonth,
                day: billingDay,
                zone: selectedZone,
                ftRate,
            });

            setRawRecords(data || []);
        } catch (err) {
            console.error('Failed to fetch power cost:', err);
            setError(err.response?.data?.message || err.message || 'Error fetching power cost data');
            setRawRecords([]);
        } finally {
            setLoading(false);
        }
    }, [selectedYear, selectedMonth, billingDay, selectedZone, ftRate]);

    useEffect(() => {
        fetchPowerCostData();
    }, [fetchPowerCostData]);

    const handleResetFilters = () => {
        setSelectedYear(currentYear);
        setSelectedMonth('all');
        setBillingDay(DEFAULT_BILLING_DAY);
        setSelectedZone('all');
        setFtRate(DEFAULT_FT_RATE);
        setActiveTab('overview');
    };

    const handleSelectMonthFromTable = (m) => {
        setSelectedMonth(m);
        setActiveTab('receipt');
    };

    // Calculate aggregated KPIs summary
    const summary = useMemo(() => {
        return calculatePowerCostSummary(rawRecords);
    }, [rawRecords]);

    // Prepare 12-month annual dataset
    const annualData = useMemo(() => {
        return buildAnnualCostChartData(rawRecords, selectedYear, t);
    }, [rawRecords, selectedYear, t]);

    // Active record for receipt view
    const activeReceiptRecord = useMemo(() => {
        if (!rawRecords || rawRecords.length === 0) return null;
        if (selectedMonth !== 'all') {
            const found = rawRecords.find((r) => r.month === parseInt(selectedMonth, 10));
            return found || rawRecords[0];
        }
        return summary?.latestRecord || rawRecords[0];
    }, [rawRecords, selectedMonth, summary]);

    const hasData = rawRecords.length > 0;

    return (
        <Box sx={{ flexGrow: 1, minHeight: '100vh', pb: 6 }}>
            <Header title={t('common.powerCostTitle', 'Electricity & Power Cost Estimator')} />

            <Container maxWidth="xl" sx={{ mt: 3, px: { xs: 2, sm: 3 } }}>
                {/* Page Title & Subtitle */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 800, letterSpacing: -0.5, mb: 0.5 }}>
                        {t('common.powerCostTitle', 'Electricity & Power Cost Estimator')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t(
                            'common.powerCostSubtitle',
                            'Detailed billing cycle analysis, progressive tiered tariff calculation, and digital MEA invoice receipt preview'
                        )}
                    </Typography>
                </Box>

                {/* Control & Filter Bar */}
                <PowerCostControlBar
                    selectedYear={selectedYear}
                    onYearChange={setSelectedYear}
                    selectedMonth={selectedMonth}
                    onMonthChange={setSelectedMonth}
                    billingDay={billingDay}
                    onBillingDayChange={setBillingDay}
                    selectedZone={selectedZone}
                    onZoneChange={setSelectedZone}
                    availableZones={availableZones}
                    ftRate={ftRate}
                    onFtRateChange={setFtRate}
                    onRefresh={fetchPowerCostData}
                    loading={loading}
                />

                {/* Error Banner */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Content Area */}
                {loading ? (
                    <PowerCostLoadingSkeleton />
                ) : !hasData ? (
                    <PowerCostEmptyState onResetFilters={handleResetFilters} />
                ) : (
                    <>
                        {/* KPI Summary Cards */}
                        <PowerCostKpiCards
                            summary={summary}
                            selectedMonth={selectedMonth}
                            selectedYear={selectedYear}
                        />

                        {/* View Tabs: Overview vs Digital Receipt */}
                        <Paper elevation={0} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'transparent' }}>
                            <Tabs
                                value={activeTab}
                                onChange={(e, val) => setActiveTab(val)}
                                aria-label="power cost views"
                                textColor="primary"
                                indicatorColor="primary"
                            >
                                <Tab
                                    icon={<BarChartIcon fontSize="small" />}
                                    iconPosition="start"
                                    label={t('common.costVsKwh', 'Cost & Consumption Charts')}
                                    value="overview"
                                    sx={{ textTransform: 'none', fontWeight: 700 }}
                                />
                                <Tab
                                    icon={<ReceiptLong fontSize="small" />}
                                    iconPosition="start"
                                    label={t('common.digitalBillReceipt', 'Digital Bill Receipt')}
                                    value="receipt"
                                    sx={{ textTransform: 'none', fontWeight: 700 }}
                                />
                            </Tabs>
                        </Paper>

                        {/* Overview Mode: Charts */}
                        {activeTab === 'overview' && (
                            <PowerCostCharts
                                annualData={annualData}
                                activeRecord={activeReceiptRecord}
                                selectedYear={selectedYear}
                                selectedMonth={selectedMonth}
                            />
                        )}

                        {/* Receipt Mode: Official Digital MEA Receipt */}
                        {activeTab === 'receipt' && (
                            <DigitalBillReceipt
                                record={activeReceiptRecord}
                                selectedZone={selectedZone}
                                selectedYear={selectedYear}
                            />
                        )}

                        {/* Detailed Breakdown Data Table */}
                        <PowerCostBreakdownTable
                            records={rawRecords}
                            selectedYear={selectedYear}
                            onSelectMonth={handleSelectMonthFromTable}
                            selectedMonth={selectedMonth}
                        />
                    </>
                )}
            </Container>
        </Box>
    );
}

export default PowerCostPage;
